import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AsyncSelect from 'react-select/async';
import { LocationDropdownIndicator, LOCATION_AUTOCOMPLETE_STYLES } from '../../../animations/selectDetails';
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng
} from 'use-places-autocomplete';

const LocationsAutocomplete = ({ setCenter, initialLocation = null, onLocationChange }) => {
    const { t } = useTranslation();
    const {
        ready,
        value,
        setValue,
        suggestions: { status, data },
        clearSuggestions,
        clearCache
    } = usePlacesAutocomplete();
    const [locationPlaceholder, setLocationPlaceholder] = useState(initialLocation?.label || 'Location');
    const [selectedLocation, setSelectedLocation] = useState(initialLocation?.label
        ? { value: initialLocation.value || initialLocation.label, label: initialLocation.label }
        : null);

    useEffect(() => {
        if (!initialLocation?.label) return;
        setSelectedLocation({ value: initialLocation.value || initialLocation.label, label: initialLocation.label });
        setLocationPlaceholder(initialLocation.label);
    }, [initialLocation?.label, initialLocation?.value]);

    const handleInputChange = (newTextValue, { action }) => {
        if (action === 'input-change') setValue(newTextValue);
    };

    const handleSelect = async option => {
        if (!option) {
            setSelectedLocation(null);
            setLocationPlaceholder('Location');
            setValue('', false);
            clearSuggestions();
            clearCache();
            onLocationChange?.(null);
            return;
        }

        const { label: address } = option;
        setSelectedLocation(option);
        setLocationPlaceholder(address);
        setValue(address, false);
        clearSuggestions();

        const results = await getGeocode({ address });
        const { lat, lng } = getLatLng(results[0]);

        const center = { lat, lng };
        setCenter(center);
        onLocationChange?.({ ...option, center });
    }

    const handleLoadOptions = async () => {
        return data.map(({ place_id, description }) => {
            return { value: place_id, label: description };
        });
    }

    return <AsyncSelect
        cacheOptions={true}
        loadOptions={handleLoadOptions}
        loadingMessage={() => `${t('Loading')}...`}
        value={selectedLocation}
        onInputChange={handleInputChange}
        placeholder={locationPlaceholder === 'Location' ? t('Location') : locationPlaceholder}
        onChange={handleSelect}
        onFocus={() => setLocationPlaceholder('')}
        onBlur={() => {locationPlaceholder.length === 0 && setLocationPlaceholder( t('Location') )}}
        isDisabled={!ready}
        isLoading={!ready}
        onSelectResetsInput={false}
        onBlurResetsInput={false}
        inputId="location-input"
        components={{ DropdownIndicator: LocationDropdownIndicator }}
        isSearchable={true}
        isClearable={true}
        styles={LOCATION_AUTOCOMPLETE_STYLES}
        noOptionsMessage={() => "no locations"}
    />;
}

export default LocationsAutocomplete;
